import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuthStore } from "../store/authStore";
import { useT } from "../i18n";
import { useFmt, fmtDate } from "../i18n/fmt";
import { type Lang } from "../i18n/langStore";
import { groupColor } from "../lib/hitos";
import ExportButtons from "../components/ExportButtons";

type DayActivity = {
  date: string;
  total: number;
  by_type: Record<string, number>;
  /** Recuento por GRUPO de hito. Clave "0" = fila sin hito mapeado. */
  by_group: Record<string, number>;
  portfolio: number;
  competitor: number;
};

/** Fuente para el filtro y el pie (contrato con Italfarmaco, punto 4: fecha de la primera comprobación). */
type SourceOpt = { code: string; name: string; is_active: boolean; monitoring_since?: string | null };

type TFn = (key: string, vars?: Record<string, string | number>) => string;

function formatDate(dateStr: string, year: number, month: number, t: TFn, months: string[], lang: Lang): string {
  const now = new Date();
  const d = new Date(dateStr + "T12:00:00");
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today); yesterday.setDate(today.getDate() - 1);
  const dDate = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  if (dDate.getTime() === today.getTime()) return t("history.today");
  if (dDate.getTime() === yesterday.getTime()) return t("history.yesterday");
  const showYear = !(year === now.getFullYear() && month === now.getMonth() + 1);
  const weekday = fmtDate(d, lang, { weekday: "long" });
  const day = d.getDate();
  const mon = months[d.getMonth()];
  return showYear ? `${weekday}, ${day} ${mon} ${d.getFullYear()}` : `${weekday}, ${day} ${mon}`;
}

export default function HistoryPage() {
  const t = useT();
  const fmt = useFmt();
  const MONTHS = Array.from({ length: 12 }, (_, i) => t(`history.month${i}`));
  const DAYS = Array.from({ length: 7 }, (_, i) => t(`history.day${i}`));
  // §90: el Histórico era la única pantalla que seguía hablando de TIPOS de
  // cambio. Ahora habla de GRUPOS de hito, con los mismos colores que Cambios y
  // el Radar. La leyenda muestra sólo los grupos presentes en el mes (son 11 y
  // no cabrían todos, y además así informa de lo que ha pasado).
  const groupLabel = (g: string) => g === "0" ? t("hitos.noHito") : t(`hitos.groupShort${g}`);
  const groupColorOf = (g: string) => g === "0" ? "#94a3b8" : groupColor(Number(g));
  // El heatmap del calendario usa el color de la org como intensidad (dato real, se conserva).
  const BRAND = useAuthStore(s => s.user?.primary_color) || "#0891b2";
  const now = new Date();
  const [year, setYear]       = useState(now.getFullYear());
  const [month, setMonth]     = useState(now.getMonth() + 1);
  const [activity, setActivity] = useState<DayActivity[]>([]);
  const [loading, setLoading]   = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [retryKey, setRetryKey] = useState(0);
  const navigate = useNavigate();
  const cardRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const [source, setSource] = useState("");
  const [sources, setSources] = useState<SourceOpt[]>([]);

  useEffect(() => {
    api.get<SourceOpt[]>("/sources")
      .then(r => setSources(r.filter(s => s.is_active).sort((a, b) => a.name.localeCompare(b.name))))
      .catch(() => setSources([]));
  }, []);

  const currentYear = now.getFullYear();
  const years = Array.from({ length: currentYear - 2009 }, (_, i) => currentYear - i);

  useEffect(() => {
    setLoading(true);
    setLoadError(false);
    api.get<DayActivity[]>(`/dashboard/activity?year=${year}&month=${month}${source ? `&source=${encodeURIComponent(source)}` : ""}`)
      .then(data => {
        const byDate: Record<string, DayActivity> = {};
        data.forEach(d => { byDate[d.date] = d; });
        const daysInMonth = new Date(year, month, 0).getDate();
        const todayStr = now.toISOString().slice(0, 10);
        const full: DayActivity[] = [];
        for (let day = daysInMonth; day >= 1; day--) {
          const key = `${year}-${String(month).padStart(2,"0")}-${String(day).padStart(2,"0")}`;
          if (key > todayStr) continue;
          full.push(byDate[key] ?? { date: key, total: 0, by_type: {}, portfolio: 0, competitor: 0 });
        }
        setActivity(full);
        setLoading(false);
      })
      .catch(() => { setLoadError(true); setLoading(false); });
  }, [year, month, source, retryKey]);

  const prevMonth = () => { if (month === 1) { setYear(y => y - 1); setMonth(12); } else setMonth(m => m - 1); };
  const nextMonth = () => { if (month === 12) { setYear(y => y + 1); setMonth(1); } else setMonth(m => m + 1); };
  const isCurrentMonth = year === now.getFullYear() && month === now.getMonth() + 1;
  const isEarliestMonth = year === 2010 && month === 1;

  const byDate: Record<string, DayActivity> = {};
  activity.forEach(d => { byDate[d.date] = d; });

  const daysWithChanges = activity.filter(d => d.total > 0).length;
  const totalChanges = activity.reduce((s, d) => s + d.total, 0);
  const maxTotal = Math.max(...activity.map(d => d.total), 1);

  // ── Calendar grid ──────────────────────────────────────────────
  const daysInMonth = new Date(year, month, 0).getDate();
  const todayStr = now.toISOString().slice(0, 10);
  // JS getDay(): 0=Sun..6=Sat → convert to Mon=0..Sun=6
  const firstDow = (new Date(year, month - 1, 1).getDay() + 6) % 7;

  const calCells: (number | null)[] = [
    ...Array(firstDow).fill(null),
    ...Array.from({ length: daysInMonth }, (_, i) => i + 1),
  ];
  // Pad to full weeks
  while (calCells.length % 7 !== 0) calCells.push(null);

  const changesUrl = (date: string) => `/changes?date=${date}${source ? `&source=${encodeURIComponent(source)}` : ""}`;
  const selected = sources.find(s => s.code === source);
  const since = (selected ? [selected] : sources).map(s => s.monitoring_since).filter((d): d is string => !!d).sort()[0];

  const scrollToDay = (dateStr: string) => {
    const el = cardRefs.current[dateStr];
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="page" style={{ color: "var(--clr-text)", fontVariantNumeric: "tabular-nums" }}>

      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: "1.4rem", fontWeight: 700, letterSpacing: "-0.02em" }}>{t("history.title")}</h1>
          <p style={{ margin: "4px 0 0", fontSize: "0.82rem", color: "var(--clr-text3)" }}>
            {loading ? t("history.loading") : t("history.subtitle", { month: MONTHS[month - 1], year, days: daysWithChanges, total: totalChanges })}
          </p>
        </div>

        {/* Month navigator */}
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
          <ExportButtons small stem={`helixscan_historico_${year}${String(month).padStart(2, "0")}`}
            path={`/export/history?year=${year}&month=${month}${source ? `&source=${encodeURIComponent(source)}` : ""}`} />
          <select value={source} onChange={e => setSource(e.target.value)} aria-label={t("history.sourceFilter")} style={{
            background: "var(--clr-input-bg)", border: "1px solid var(--clr-border)", maxWidth: 240,
            borderRadius: 999, color: "var(--clr-text)", padding: "0.42rem 1rem", fontSize: "0.78rem", outline: "none", cursor: "pointer",
          }}>
            <option value="">{t("history.allSources")}</option>
            {sources.map(s => <option key={s.code} value={s.code}>{s.name}</option>)}
          </select>
          <select value={year} onChange={e => setYear(Number(e.target.value))} style={{
            background: "var(--clr-input-bg)", border: "1px solid var(--clr-border)",
            borderRadius: 999, color: "var(--clr-text)", padding: "0.42rem 1rem", fontSize: "0.78rem", outline: "none", cursor: "pointer",
          }}>
            {years.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          <button onClick={prevMonth} disabled={isEarliestMonth} aria-label={t("history.prevMonth")} style={{
            background: "var(--clr-surface)", border: "1px solid var(--clr-border)", borderRadius: 999,
            width: 32, height: 32, display: "flex", alignItems: "center", justifyContent: "center",
            cursor: isEarliestMonth ? "not-allowed" : "pointer",
            color: isEarliestMonth ? "var(--clr-text4)" : "var(--clr-text)", opacity: isEarliestMonth ? 0.4 : 1,
          }}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6" /></svg>
          </button>
          <div style={{
            background: "var(--clr-surface)", border: "1px solid var(--clr-border)",
            borderRadius: 999, padding: "0.42rem 1.1rem", fontSize: "0.82rem", fontWeight: 600,
            minWidth: 130, textAlign: "center", color: "var(--clr-text)",
          }}>{MONTHS[month - 1]} {year}</div>
          <button onClick={nextMonth} disabled={isCurrentMonth} aria-label={t("history.nextMonth")} style={{
            background: "var(--clr-surface)", border: "1px solid var(--clr-border)", borderRadius: 999,
            width: 32, height: 32, display: "flex", alignItems: "center", justifyContent: "center",
            cursor: isCurrentMonth ? "not-allowed" : "pointer",
            color: isCurrentMonth ? "var(--clr-text4)" : "var(--clr-text)", opacity: isCurrentMonth ? 0.4 : 1,
          }}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18l6-6-6-6" /></svg>
          </button>
          {!isCurrentMonth && (
            <button onClick={() => { setYear(now.getFullYear()); setMonth(now.getMonth() + 1); }} style={{
              background: "var(--clr-org-primary)", color: "#fff", border: 0, borderRadius: 999,
              padding: "0.42rem 1.1rem", fontSize: "0.78rem", fontWeight: 600, cursor: "pointer",
            }}>{t("history.today")}</button>
          )}
        </div>
      </div>

      {loadError && (
        <div style={{ display: "flex", alignItems: "center", gap: 12, background: "var(--clr-surface)", border: "1px solid var(--clr-border)", borderRadius: 18, padding: "12px 16px", marginBottom: 16, flexWrap: "wrap", backdropFilter: "var(--clr-blur)", WebkitBackdropFilter: "var(--clr-blur)", boxShadow: "var(--clr-shadow-sm)" }}>
          <span style={{ width: 8, height: 8, borderRadius: 4, background: "#ef4444", flexShrink: 0 }} />
          <span style={{ flex: 1, fontSize: "0.85rem", color: "var(--clr-text2)", minWidth: 180 }}>{t("history.loadError")}</span>
          <button onClick={() => setRetryKey(k => k + 1)} style={{ background: "var(--clr-org-primary)", color: "#fff", border: 0, borderRadius: 999, padding: "8px 18px", fontSize: "0.8rem", fontWeight: 600, cursor: "pointer" }}>{t("history.retry")}</button>
        </div>
      )}

      {/* ── Calendar ── */}
      {!loading && !loadError && (
        <div style={{
          background: "var(--clr-surface)", border: "1px solid var(--clr-border)",
          borderRadius: 18, padding: "16px 20px", marginBottom: 24,
          backdropFilter: "var(--clr-blur)", WebkitBackdropFilter: "var(--clr-blur)", boxShadow: "var(--clr-shadow-sm)",
        }}>
          {/* Day-of-week headers */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 4, marginBottom: 6 }}>
            {DAYS.map((d, i) => (
              <div key={i} style={{ textAlign: "center", fontSize: "0.68rem", fontWeight: 700, color: "var(--clr-text4)", padding: "4px 0" }}>{d}</div>
            ))}
          </div>

          {/* Day cells */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 4 }}>
            {calCells.map((day, idx) => {
              if (!day) return <div key={`empty-${idx}`} />;

              const dateStr = `${year}-${String(month).padStart(2,"0")}-${String(day).padStart(2,"0")}`;
              const isFuture = dateStr > todayStr;
              const isToday = dateStr === todayStr;
              const dayData = byDate[dateStr];
              const hasChanges = !isFuture && dayData && dayData.total > 0;
              const total = dayData?.total ?? 0;

              // Heat intensity based on number of changes
              const intensity = hasChanges ? Math.min(total / maxTotal, 1) : 0;
              const bgAlpha = hasChanges ? Math.round(20 + intensity * 60).toString(16).padStart(2,"0") : "00";

              return (
                <div
                  key={dateStr}
                  onClick={() => {
                    if (hasChanges) scrollToDay(dateStr);
                    else if (!isFuture) navigate(changesUrl(dateStr));
                  }}
                  style={{
                    textAlign: "center", borderRadius: 8, padding: "6px 4px",
                    cursor: isFuture ? "default" : "pointer",
                    // El día de hoy/seleccionado se distingue SOLO por el borde azul
                    // (sin relleno), para no confundirse con el relleno de los días
                    // que tienen cambios.
                    background: isToday ? "transparent" : hasChanges ? BRAND + bgAlpha : "transparent",
                    border: isToday ? `2px solid ${BRAND}` : "2px solid transparent",
                    transition: "background 0.15s",
                    opacity: isFuture ? 0.25 : 1,
                  }}
                  onMouseEnter={e => { if (!isFuture) (e.currentTarget as HTMLDivElement).style.background = BRAND + "28"; }}
                  onMouseLeave={e => {
                    (e.currentTarget as HTMLDivElement).style.background =
                      isToday ? "transparent" : hasChanges ? BRAND + bgAlpha : "transparent";
                  }}
                >
                  <div style={{
                    fontSize: "0.82rem", fontWeight: isToday || hasChanges ? 700 : 500,
                    color: isToday ? BRAND : hasChanges ? BRAND : "var(--clr-text3)",
                    lineHeight: 1.2,
                  }}>{day}</div>
                  {hasChanges && (
                    <div style={{ fontSize: "0.6rem", color: BRAND, fontWeight: 600, marginTop: 2, opacity: 0.8 }}>
                      {total}
                    </div>
                  )}
                  {hasChanges && (
                    <div style={{ display: "flex", justifyContent: "center", gap: 2, marginTop: 3, flexWrap: "wrap" }}>
                      {Object.keys(dayData.by_group ?? {}).slice(0, 3).map(g => (
                        <div key={g} style={{
                          width: 5, height: 5, borderRadius: "50%",
                          background: groupColorOf(g),
                        }} />
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Leyenda: sólo los grupos que han ocurrido este mes, de más a menos */}
          {(() => {
            const total: Record<string, number> = {};
            for (const d of activity) {
              for (const [g, n] of Object.entries(d.by_group ?? {})) total[g] = (total[g] ?? 0) + n;
            }
            const grupos = Object.entries(total).sort((a, b) => b[1] - a[1]);
            if (grupos.length === 0) return null;
            return (
              <div style={{ display: "flex", flexWrap: "wrap", gap: "6px 8px", marginTop: 14, paddingTop: 12, borderTop: "1px solid var(--clr-border2)" }}>
                {grupos.map(([g, n]) => {
                  const col = groupColorOf(g);
                  return (
                    <span key={g} style={{
                      display: "inline-flex", alignItems: "center", gap: 5, fontSize: "0.68rem", fontWeight: 500,
                      background: col + "1e", color: col,
                      borderRadius: 999, padding: "2px 9px", whiteSpace: "nowrap",
                    }}>
                      <span style={{ width: 6, height: 6, borderRadius: "50%", background: col, flexShrink: 0 }} />
                      {groupLabel(g)}
                      <span style={{ fontWeight: 700 }}>{fmt.num(n)}</span>
                    </span>
                  );
                })}
              </div>
            );
          })()}
        </div>
      )}

      {loading && <div style={{ textAlign: "center", padding: 60, color: "var(--clr-text4)" }}>{t("history.loading")}</div>}

      {/* ── Only days with changes ── */}
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {activity.filter(day => day.total > 0).map(day => (
          <div
            key={day.date}
            ref={el => { cardRefs.current[day.date] = el; }}
            onClick={() => navigate(changesUrl(day.date))}
            style={{
              background: "var(--clr-surface)", border: "1px solid var(--clr-border)",
              borderRadius: 18, padding: "1rem 1.25rem",
              backdropFilter: "var(--clr-blur)", WebkitBackdropFilter: "var(--clr-blur)", boxShadow: "var(--clr-shadow-sm)",
              cursor: "pointer", transition: "transform 0.25s cubic-bezier(.25,.6,.3,1), box-shadow 0.25s",
            }}
            onMouseEnter={e => {
              (e.currentTarget as HTMLDivElement).style.transform = "translateY(-2px)";
              (e.currentTarget as HTMLDivElement).style.boxShadow = "var(--clr-shadow)";
            }}
            onMouseLeave={e => {
              (e.currentTarget as HTMLDivElement).style.transform = "";
              (e.currentTarget as HTMLDivElement).style.boxShadow = "var(--clr-shadow-sm)";
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
              <div style={{ minWidth: 52, textAlign: "center", background: "var(--clr-surface2)", border: "1px solid var(--clr-border2)", borderRadius: 12, padding: "6px 10px" }}>
                <div style={{ fontSize: "1.4rem", fontWeight: 700, letterSpacing: "-0.02em", color: "var(--clr-text)", lineHeight: 1 }}>
                  {new Date(day.date + "T12:00:00").getDate()}
                </div>
                <div style={{ fontSize: "0.6rem", color: "var(--clr-text4)", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px" }}>
                  {fmt.date(new Date(day.date + "T12:00:00"), { month: "short" })}
                </div>
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "baseline", gap: 14, marginBottom: 8, flexWrap: "wrap" }}>
                  <span style={{ fontSize: "0.9rem", fontWeight: 600, color: "var(--clr-text)", textTransform: "capitalize" }}>
                    {formatDate(day.date, year, month, t, MONTHS, fmt.lang)}
                  </span>
                  {day.portfolio > 0 && (
                    <span style={{
                      display: "inline-flex", alignItems: "center", gap: 5, fontSize: "0.68rem", fontWeight: 600,
                      background: "var(--clr-nav-active-bg, rgba(8,145,178,0.12))", color: "var(--clr-org-primary)",
                      borderRadius: 999, padding: "2px 9px", whiteSpace: "nowrap",
                    }}>
                      <span style={{ width: 6, height: 6, borderRadius: 3, background: "var(--clr-org-primary)" }} />
                      {fmt.num(day.portfolio)} {t("history.portfolio")}
                    </span>
                  )}
                  {day.competitor > 0 && (
                    <span style={{
                      display: "inline-flex", alignItems: "center", gap: 5, fontSize: "0.68rem", fontWeight: 600,
                      background: "#f59e0b1e", color: "#f59e0b",
                      borderRadius: 999, padding: "2px 9px", whiteSpace: "nowrap",
                    }}>
                      <span style={{ width: 6, height: 6, borderRadius: 3, background: "#f59e0b" }} />
                      {fmt.num(day.competitor)} {t("history.competitor")}
                    </span>
                  )}
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "5px 8px" }}>
                  {Object.entries(day.by_group ?? {}).sort((a, b) => b[1] - a[1]).map(([g, count]) => {
                    const tc = groupColorOf(g);
                    return (
                      <span key={g} style={{
                        display: "inline-flex", alignItems: "center", gap: 5, fontSize: "0.7rem", fontWeight: 500,
                        background: tc + "1e", color: tc,
                        borderRadius: 999, padding: "2px 9px", whiteSpace: "nowrap",
                      }}>
                        <span style={{ fontWeight: 700, letterSpacing: "-0.01em" }}>{fmt.num(count)}</span>
                        {groupLabel(g)}
                      </span>
                    );
                  })}
                </div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
                <span style={{ display: "inline-flex", alignItems: "baseline", gap: 5, whiteSpace: "nowrap" }}>
                  <span style={{ fontSize: "0.9rem", fontWeight: 700, letterSpacing: "-0.02em", color: "var(--clr-text)" }}>{fmt.num(day.total)}</span>
                  <span style={{ fontSize: "0.68rem", color: "var(--clr-text4)" }}>{t("history.changes")}</span>
                </span>
                <span style={{ color: "var(--clr-text4)", display: "flex" }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18l6-6-6-6" /></svg>
                </span>
              </div>
            </div>
          </div>
        ))}

        {!loading && !loadError && activity.filter(d => d.total > 0).length === 0 && (
          <div style={{ textAlign: "center", padding: 40, color: "var(--clr-text4)", fontSize: "0.85rem" }}>
            {t("history.noChanges", { month: MONTHS[month - 1], year })}
          </div>
        )}
      </div>

      {/* Pie: desde cuándo vigila HelixScan (contrato con Italfarmaco, punto 4). El histórico
          conserva todos los eventos desde la primera comprobación de cada fuente. */}
      {since && (
        <p style={{ margin: "18px 0 0", fontSize: "0.75rem", color: "var(--clr-text4)", lineHeight: 1.5 }}>
          {selected
            ? t("history.footerSource", { name: selected.name, date: fmt.date(new Date(since)) })
            : t("history.footerAll", { date: fmt.date(new Date(since)) })}
        </p>
      )}
    </div>
  );
}
